import { Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnnouncementService } from '../../shared/services/announcement.service';
import { CustomMessageService } from '.././../../core/message.service';
import { AnnouncementModerationAttributeEnum } from '../../shared/enums/announcement-enums';
import {
  AnnouncementInfoSkillViewModel,
  AnnouncementModerationChanges,
  AnnouncementModerationDetails,
  AnnouncementProduct,
  AnnouncementSkillChanges,
  AnnouncmentModerationRequestViewModel,
} from '../../shared/models/announcement-moderation-model';
import {
  SkillItem,
  TravelRequirements,
} from '../../shared/models/announcement-info-model';
import { ApplicationMessageSource } from './../../../core/models/application-message-source';
import { MessageType } from './../../../core/models/enum/message-type';
import { ApplicationMessage } from './../../../core/models/application-message';
import { CommonLookupItem } from '../../../../app/shared/models/common-lookup-item.model';
import { TranslateService } from '@ngx-translate/core';
import { WorkExperienceTypeEnum } from '../../shared/enums/work-experience-type-enum';
import { AuthService } from '@admin/core/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-announcement-info',
  templateUrl: './announcement-info.component.html',
  styleUrls: ['./announcement-info.component.less'],
  standalone: false,
})
export class AnnouncementInfoComponent {
  @Input() alternativeLanguage!: CommonLookupItem<number, string>[];
  @Input() announcementModerationId!: string;
  @Input() products!: AnnouncementProduct;
  @Input() editedDescription!: string;
  @Input() editedDescription2!: string;
  @Input() isCreate: boolean;
  @Input() isEdited: boolean;
  @Input() announcement: AnnouncementModerationDetails;
  @Input() changes: AnnouncementModerationChanges;
  @Output() refreshPageEvent = new EventEmitter<any>();
  public applicationRuleEnum: any;
  public entityAttributes = AnnouncementModerationAttributeEnum;
  public workExperienceTypeEnum: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private announcementService: AnnouncementService,
    private authService: AuthService,
    private translateService: TranslateService,
    private messageService: CustomMessageService,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.applicationRuleEnum = ApplicationMethodEnum;
    this.workExperienceTypeEnum = WorkExperienceTypeEnum;
  }

  onApprove(announcementModerationId) {
    if (!confirm(this.translateService.instant('message.AreYouSure'))) {
      return;
    }

    this.announcementService
      .approveAnnouncementModeration(announcementModerationId)
      .subscribe((d: any) => {
        if (d.success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: this.translateService.instant('message.SaveMessage'),
          });
          this.router.navigate(['./announcements/list']);
        } else if (!d.success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Error,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
        }
      });
  }

  onReject(announcementModerationId) {
    if (!confirm(this.translateService.instant('message.AreYouSure'))) {
      return;
    }
    this.announcementService
      .rejectAnnouncementModeration(announcementModerationId)
      .subscribe((d: any) => {
        if (d.success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: this.translateService.instant('message.SaveMessage'),
          });
          this.router.navigate(['./announcements/list']);
        } else if (!d.success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Error,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
        }
      });
  }

  onLoginAsCustomer(customerId: number){
    this.authService.loginAsCustomer(customerId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      //display message
    });
  }

  isChanged(attribute: AnnouncementModerationAttributeEnum): boolean {
    return (
      this.isEdited && !!this.changes?.entityAttributes?.includes(attribute)
    );
  }

  isAnyChanged(...attributes: AnnouncementModerationAttributeEnum[]): boolean {
    return attributes.some((attribute) => this.isChanged(attribute));
  }

  get travelRequirements(): TravelRequirements | undefined {
    return this.announcement?.announcementRequirements?.travelRequirements;
  }

  get travelRequirementsText(): string {
    const travel = this.travelRequirements;
    const isRequired = this.translateService.instant(
      travel?.isTravelRequired ? 'label.Yes' : 'label.No'
    );
    const percentage = travel?.travelPercentage
      ? '(' + travel.travelPercentage + '%) '
      : '';
    const domestic = this.translateService.instant('label.Domestic');
    const abroad = this.translateService.instant('label.Abroad');
    let direction = '';

    if (travel?.isTravelDomestic && travel?.isTravelAbroad) {
      direction = '(' + domestic + '/' + abroad + ')';
    } else if (travel?.isTravelAbroad) {
      direction = '(' + abroad + ')';
    } else if (travel?.isTravelDomestic) {
      direction = '(' + domestic + ')';
    }

    return isRequired + percentage + direction;
  }

  get drivingLicences(): string[] {
    return this.announcement?.drivingLicenses ?? [];
  }

  get hardSkills(): SkillItem[] {
    return this.announcement?.employerRequirements?.hardSkills ?? [];
  }

  get softSkills(): SkillItem[] {
    return this.announcement?.employerRequirements?.softSkills ?? [];
  }

  get hardSkillChanges(): AnnouncementSkillChanges {
    return this.changes?.announcementHardSkillChanges;
  }

  get softSkillChanges(): AnnouncementSkillChanges {
    return this.changes?.announcementSoftSkillChanges;
  }

  get removedHardSkills(): AnnouncementInfoSkillViewModel[] {
    return this.hardSkillChanges?.removedSkillViewModels ?? [];
  }

  get removedSoftSkills(): AnnouncementInfoSkillViewModel[] {
    return this.softSkillChanges?.removedSkillViewModels ?? [];
  }

  get showHardSkillsPlaceholder(): boolean {
    return (
      !this.hardSkills?.length &&
      !(this.isEdited && this.removedHardSkills.length)
    );
  }

  get showSoftSkillsPlaceholder(): boolean {
    return (
      !this.softSkills?.length &&
      !(this.isEdited && this.removedSoftSkills.length)
    );
  }

  isAdded(skill: SkillItem, skillChanges: AnnouncementSkillChanges): boolean {
    return !!skillChanges?.addedSkills?.some((val) => val.index === skill.index);
  }

  isCustom(skill: SkillItem): boolean {
    return skill.canonicalSkillId === null;
  }

  isRemovedCustom(skill: AnnouncementInfoSkillViewModel): boolean {
    return !skill.skillId;
  }

  skillName(skill: SkillItem): string {
    return !this.isCustom(skill)
      ? skill.name
      : skill.userProvidedText;
  }

  removedSkillName(skill: AnnouncementInfoSkillViewModel): string {
    return !this.isRemovedCustom(skill)
      ? skill.name
      : skill.userProvidedText;
  }

  isMandatory(name: string, isMandatory: boolean) {
    if (isMandatory) {
      return (
        name + '(' + this.translateService.instant('label.Mandatory') + ')'
      );
    } else {
      return name + '(' + this.translateService.instant('label.Desired') + ')';
    }
  }

  refreshPage() {
    this.refreshPageEvent.emit();
  }

  ngOnInit() {}
}

export enum ApplicationMethodEnum {
  MobilePostAddress = 1,
  ViaWebsite = 2,
  ViaLink = 3,
}
